import { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { RankItem } from '@/components/ui/RankList';

// Re-exports so tabs can pull everything they need from ../shared.
export { LoadingSpinner };
export type { RankItem };

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
// Chart colours come from the --chart-* tokens (index.css :root), with the
// old literal as the fallback (no document, or a token missing). The accent
// roles follow the theme's --primary.
const _css = (name: string, fallback: string) => (typeof document === 'undefined' ? fallback
  : getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback);
const _accent = () => _css('--primary', '#1f8ba3');
export const COLORS = {
  get revenue() { return _css('--chart-revenue', '#10b981'); },
  get expense() { return _css('--chart-expense', '#f43f5e'); },
  get net() { return _accent(); },
  get primary() { return _accent(); },
  get cy() { return _css('--chart-income', '#0a1628'); },
  get py() { return _css('--chart-prior', '#6b93bf'); },
  get cumulative() { return _css('--chart-trend', '#f59e0b'); },
  get grid() { return _css('--chart-grid', '#eef0f5'); },
  get tick() { return _css('--chart-tick', '#64748b'); },
};
// The floor for chart text (axis ticks, bar labels, legends), in px.
export const TICK = 11;
export const fmtK = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${Math.round(n / 1000)}K`;
  return String(Math.round(n));
};
export const fmt = fmtK;
// Value-axis ticks: the sign as a true minus (U+2212, the width of a digit, so
// it is never mistaken for a dash or lost), and none on a value that rounds to 0.
export const axisFmt = (n: number) => {
  const s = fmtK(Math.abs(n));
  return n < 0 && s !== '0' ? `−${s}` : s;
};
// The percentage change from a prior figure, for every Analytics tab and Home
// card: null (no change to show) when the prior figure is not positive, is
// under one currency unit, or is under 1% of this period's figure, where the
// percentage says nothing (a prior net of a few cents read "↑ 5177526700.0%").
export function pctChange(cy: number, py: number): number | null {
  if (!(py > 0) || py < 1 || py < Math.abs(cy) * 0.01) return null;
  return ((cy - py) / py) * 100;
}
// A change of 1000% or more reads ">999%" (a fall, "<−999%").
export const PCT_CAP = 1000;
// pctChange as table text: "—" for none, one decimal otherwise ("+" with signed).
export function pctChangeText(cy: number, py: number, signed = false): string {
  const v = pctChange(cy, py);
  if (v == null) return '—';
  if (v >= PCT_CAP) return '>999%';
  if (v <= -PCT_CAP) return '<−999%';
  return `${signed && v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}
export const fmtPct = (n: number | null | undefined) => (n == null ? '—' : `${n > 0 ? '+' : ''}${n}%`);
// Linear-regression trend across the months that actually have data (>0), so
// the dashed overlay reads as a smooth line, not the jagged bar tops. Returns
// null for empty months so the line only spans the active range.
export function linTrend(vals: number[]): (number | null)[] {
  const pts = vals.map((y, x) => ({ x, y })).filter((p) => p.y > 0);
  if (pts.length < 2) return vals.map(() => null);
  const n = pts.length;
  const sx = pts.reduce((s, p) => s + p.x, 0), sy = pts.reduce((s, p) => s + p.y, 0);
  const sxx = pts.reduce((s, p) => s + p.x * p.x, 0), sxy = pts.reduce((s, p) => s + p.x * p.y, 0);
  const denom = n * sxx - sx * sx;
  if (denom === 0) return vals.map(() => null);
  const b = (n * sxy - sx * sy) / denom, a = (sy - b * sx) / n;
  const lo = pts[0].x, hi = pts[pts.length - 1].x;
  return vals.map((_, i) => (i >= lo && i <= hi ? Math.max(0, a + b * i) : null));
}

// This month so far against the same days of the same month last year (the
// API has monthly totals only, charts-06): last year's month is scaled by its
// own length (29 days in a leap February), never past its end. Any other year
// than the current one compares whole months (ratio 1).
export function monthToDate(rows: Array<{ cy: number; py: number }>, now: Date, year?: number): { cy: number; pyProRata: number; day: number; days: number } {
  const i = now.getMonth();
  const target = year ?? now.getFullYear();
  const current = target === now.getFullYear();
  const days = new Date(target, i + 1, 0).getDate();
  const pyDays = new Date(target - 1, i + 1, 0).getDate();
  const day = current ? now.getDate() : days;
  const py = rows[i]?.py ?? 0;
  const share = current ? Math.min(day, pyDays) / pyDays : 1;
  return { cy: rows[i]?.cy ?? 0, pyProRata: Math.round(py * share * 100) / 100, day, days };
}

export const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// The month-to-date legend: "This month" / "Same days last year" for the
// current year; a past year compares whole months, so it names them.
export function mtdLegend(now: Date, year?: number): [string, string] {
  const target = year ?? now.getFullYear();
  if (target === now.getFullYear()) return ['This month', 'Same days last year'];
  const m = MONTH_SHORT[now.getMonth()];
  return [`${m} ${target}`, `${m} ${target - 1}`];
}

// The month-to-date badge's comparison: the same days for the current year;
// a past year compares whole months, so it names last year's month.
export function mtdNote(now: Date, year?: number): string {
  const target = year ?? now.getFullYear();
  if (target === now.getFullYear()) return 'vs same days last year';
  return `vs ${MONTH_SHORT[now.getMonth()]} ${target - 1}`;
}

export type Filters = {
  year: number; quarter: number[]; month: number[];
  customer: string; vendor: string; product: string; status: string; branch: string;
  // YoY "Compare years" multi-select (empty = default to year + prior year).
  years: number[];
};
export function qs(f: Filters) {
  const parts = [`year=${f.year}`];
  if (f.quarter.length) parts.push(`quarter=${f.quarter.join(',')}`);
  if (f.month.length) parts.push(`month=${f.month.join(',')}`);
  if (f.years && f.years.length) parts.push(`years=${f.years.join(',')}`);
  for (const k of ['customer', 'vendor', 'product', 'status', 'branch'] as const) {
    if (f[k]) parts.push(`${k}=${encodeURIComponent(f[k])}`);
  }
  return `?${parts.join('&')}`;
}
export function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return v;
}
export function downloadCsv(name: string, header: string[], rows: (string | number)[][]) {
  const esc = (s: string | number) => `"${String(s).replace(/"/g, '""')}"`;
  const body = [header.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([body], { type: 'text/csv' }));
  const a = document.createElement('a'); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------------------------------------------------------------------------
// *Data types
// ---------------------------------------------------------------------------
export type SummaryData = {
  currency: string; year: number;
  kpis: { revenue: number; expense: number; net: number; margin: number; cash: number; ar: number; ap: number; invoices: number; customers: number; avg_invoice: number; units: number; active_clients: number; avg_per_client: number; vs_last_month: number | null; growth_ytd: number | null };
  trend: Array<{ month: string; revenue: number; expense: number; net: number }>;
  revenue_cy_py: Array<{ month: string; cy: number; py: number }>;
};
export type CustData = { currency: string; year: number; new_customers: number; new_customer_list?: Array<{ id: string; code: string; name: string; created_at: string; revenue: number; invoices: number }>; total_revenue: number; rows: Array<{ id: string; code: string; name: string; revenue: number; invoices: number; avg_invoice: number; ar: number }> };
export type ProdData = { currency: string; year: number; rows: Array<{ id: string; code: string; name: string; revenue: number; qty: number; invoices: number }> };
export type GrowthData = { currency: string; year: number; rows: Array<{ month: string; revenue: number; growth: number | null; new_customers: number }> };
export type YoYData = { currency: string; year: number; rows: Array<{ month: string; revenue_cy: number; revenue_py: number; expense_cy: number; expense_py: number }>; totals: { revenue_cy: number; revenue_py: number; expense_cy: number; expense_py: number };
  years?: number[];
  series?: Array<{ year: number; revenue: number; expense: number }>;
  customers?: Array<{ id: string | null; name: string; byYear: Record<string, number> }>;
  products?: Array<{ id: string | null; name: string; byYear: Record<string, number> }>;
};
export type SalesData = { currency: string; year: number; kpis: { orders: number; bookings: number; open_orders: number; open_value: number; avg_order: number }; by_status: Array<{ status: string; n: number; value: number }>; monthly: Array<{ month: string; value: number }>; top_customers: Array<{ id: string; name: string; orders: number; value: number }>; top_products: Array<{ id: string | null; name: string; amount: number; qty: number }> };
export type PurchData = { currency: string; year: number; kpis: { spend: number; pos: number; pending: number; open_value: number; vendors: number; items: number; lines: number; avg_po: number }; delivery: { received_qty: number; pending_qty: number; received_pct: number }; monthly: Array<{ month: string; value: number }>; vendors: Array<{ id: string; name: string; total: number; pos: number; avg_po: number; received_pct: number }>; top_items: Array<{ id: string | null; name: string; spend: number; qty: number }> };

export type KpiDetail = { title: string; color: string; headers: string[]; rows: string[][] };

// ---------------------------------------------------------------------------
// Reusable fragments
// ---------------------------------------------------------------------------
export function withCumulative(rows: Array<{ month: string; value: number }>) {
  let acc = 0;
  return rows.map((r) => { acc += r.value; return { ...r, cumulative: Math.round(acc * 100) / 100 }; });
}

// ===========================================================================
// FinComp (priority-agent) visual primitives — faithful, typed ports.
// These reproduce the FinComp dashboard's exact sizes/colors/typography so
// Fritech tabs can match it pixel-for-pixel. English-only, recharts +
// Tailwind/inline-style. See /tmp/pa/src/{shared/KPICard,components/*}.
// ===========================================================================

// Teal-led accent palette (de-purpled for the Teal theme).
export const FC_COLORS = {
  amber: '#d97706', green: '#059669', red: '#e11d48', violet: '#1f8ba3',
  cyan: '#0891b2', blue: '#2563eb', indigo: '#3b6fb0', teal: '#0d9488',
} as const;

// Teal-led series palette for FinComp pies / pipelines (top customers,
// products…). Teal leads; the ramp stays distinguishable with no purples.
export const FC_SERIES = [
  '#1f8ba3', '#2563eb', '#10b981', '#d97706', '#db2777',
  '#0891b2', '#65a30d', '#e11d48', '#0d9488', '#f59e0b',
];
